// State Manager - 状态管理器
import storage from '../../storage';
import { DEFAULT_STARTING_ITEMS, DEFAULT_STARTING_SKILLS } from '../config';
import { LOCATION_NAMES } from '../exploration/exploration-data.js';

class StateManager {
  constructor() {
    this.state = {
      // player must choose a name on first entry
      name: '',
      isNamed: false,
      // 灵根系统
      spiritualRoot: {
        type: '', // 'single', 'mixed', 'waste'
        name: '', // 显示名称
        description: '', // 说明
        cultivationSpeed: 1.0, // 修炼速度倍率
        purified: false, // 是否已提纯
        purifiedType: '' // 提纯后的类型
      },
      sect: '无名门派',
      realm: 0, // 大境界: 0=炼气, 1=筑基, 2=金丹, 3=元婴, 4=化神, 5=渡劫, 6=真仙
      substage: 0, // 小阶段: 0=前, 1=中, 2=后, 3=巅峰
      xp: 0,
      qi: 80, // 100 * 1 * 0.8
      maxQi: 80,
      attack: 8, // 10 * 1 * 0.8
      defense: 4, // 5 * 1 * 0.8
      mana: 40, // 50 * 1 * 0.8
      divineSenseStrength: 80, // 100 * 1 * 0.8
      location: LOCATION_NAMES.UNKNOWN,
      previousLocation: LOCATION_NAMES.UNKNOWN, // 保存进入禁地前的位置
      bag: {}, // 改为对象存储物品数量 {itemName: count}
      skills: {},
      // 炼丹炼器等级相关
      pillRefiningLevel: 0, // 炼丹等级: 0=凡阶, 1=黄阶, 2=玄阶, 3=地阶, 4=天阶
      treasureRefiningLevel: 0, // 炼器等级: 0=凡阶, 1=黄阶, 2=玄阶, 3=地阶, 4=天阶
      // 禁地图相关
      inForbiddenMap: false,
      mapSize: 0,
      mapGrid: [], // 内部地图，不显示
      playerPos: {x: 0, y: 0},
      treasurePos: {x: 0, y: 0},
      exploredCells: [], // 记录探索过的格子
      scannedCells: [], // 记录扫描过的格子
      maxExplorationCompletion: 0, // 历史最高完成度
      // 探索相关
      explorationState: null,
      totalDiscoveries: [],
      totalExplorationCount: 0,
      // 炼丹炼器相关
      inRefiningPill: false,
      inRefiningTreasure: false,
      refiningItemName: '',
      // 聚宝阁相关
      inTreasureHall: false,
      unlockedPills: ['聚气丹', '回气丹'], // 初始解锁的丹方
      // 宗门相关
      inSectModal: false,
      // 状态相关
      inStatusModal: false,
      statusEffects: {}, // 状态效果 {effectName: {appliedAt: timestamp, ...effectData}}
      originalMaxQi: 80 // 原始最大气血，用于重伤计算
    };

    // 加载保存的状态
    this.loadState();
  }

  // 获取状态
  getState() {
    return this.state;
  }

  // 更新状态
  updateState(updates) {
    Object.assign(this.state, updates);
    // 立即保存以确保状态持久化
    this.saveState();
  }

  // 加载状态
  loadState() {
    const saved = storage.loadState();
    if (saved) {
      Object.assign(this.state, saved);
    }
  }

  // 保存状态
  saveState() {
    try {
      storage.saveState(this.state);
    } catch (e) {
      console.error('自动保存失败：', e.message);
    }
  }

  // 重置状态
  resetState() {
    this.state.realm = 0;
    this.state.substage = 0;
    this.state.xp = 0;
    this.state.qi = this.state.maxQi = 80;
    this.state.attack = 8;
    this.state.defense = 4;
    this.state.mana = 40;
    this.state.divineSenseStrength = 80;
    this.state.bag = { ...DEFAULT_STARTING_ITEMS };
    this.state.skills = { ...DEFAULT_STARTING_SKILLS };
    this.state.pillRefiningLevel = 0; // 炼丹等级重置为凡阶
    this.state.treasureRefiningLevel = 0; // 炼器等级重置为凡阶
    this.state.location = LOCATION_NAMES.UNKNOWN;
    this.state.name = '少侠';
    this.state.isNamed = false;
    this.state.inForbiddenMap = false;
    this.state.scannedCells = [];
    this.state.inRefiningPill = false;
    this.state.inRefiningTreasure = false;
    this.state.refiningItemName = '';
    this.state.inTreasureHall = false;
    this.state.inSectModal = false;
    this.saveState();
  }

  // 检查是否已命名
  isNamed() {
    return this.state.isNamed;
  }

  // 分配灵根
  assignSpiritualRoot() {
    const rand = Math.random() * 10; // 0-10随机数
    let rootType, rootName, description, cultivationSpeed;

    if (rand < 1) { // 10% 单灵根
      rootType = 'single';
      rootName = '单灵根';
      description = '天地间罕见的单属性灵根，修炼速度极快，可提纯为天灵根。';
      cultivationSpeed = 2.0; // 修炼速度2倍
    } else if (rand < 7) { // 60% 杂灵根
      rootType = 'mixed';
      rootName = '杂灵根';
      description = '多种属性混合的灵根，修炼速度一般，可提纯为异灵根。';
      cultivationSpeed = 1.0; // 修炼速度1倍
    } else { // 30% 废灵根
      rootType = 'waste';
      rootName = '废灵根';
      description = '五行属性混乱的灵根，修炼速度缓慢，可提纯为五行灵根。';
      cultivationSpeed = 0.5; // 修炼速度0.5倍
    }

    this.state.spiritualRoot = {
      type: rootType,
      name: rootName,
      description: description,
      cultivationSpeed: cultivationSpeed,
      purified: false,
      purifiedType: ''
    };
  }

  // 设置名字
  setName(name) {
    if (!name) return false;
    this.state.name = name;
    this.state.isNamed = true;
    // 分配灵根
    this.assignSpiritualRoot();
    this.saveState();
    return true;
  }

  // 获取名字
  getName() {
    return this.state.name;
  }

  // 更新位置
  setLocation(location) {
    this.state.location = location;
    this.saveState();
  }

  // 获取位置
  getLocation() {
    return this.state.location;
  }

  // 检查是否在禁地
  isInForbiddenMap() {
    return this.state.inForbiddenMap;
  }

  // 进入禁地
  enterForbiddenMap() {
    this.state.previousLocation = this.state.location;
    this.state.inForbiddenMap = true;
    this.state.location = LOCATION_NAMES.FORBIDDEN;
    this.saveState();
  }

  // 退出禁地
  exitForbiddenMap() {
    this.state.inForbiddenMap = false;
    this.state.location = this.state.previousLocation;
    this.saveState();
  }

  // 获取背包
  getBag() {
    return this.state.bag;
  }

  // 添加物品到背包
  addItemToBag(itemName, count = 1) {
    if (this.state.bag[itemName]) {
      this.state.bag[itemName] += count;
    } else {
      this.state.bag[itemName] = count;
    }
    this.saveState();
  }

  // 移除物品从背包
  removeItemFromBag(itemName, count = 1) {
    if (this.state.bag[itemName]) {
      this.state.bag[itemName] -= count;
      if (this.state.bag[itemName] <= 0) {
        delete this.state.bag[itemName];
      }
      this.saveState();
    }
  }

  // 检查物品是否存在
  hasItem(itemName, count = 1) {
    return (this.state.bag[itemName] || 0) >= count;
  }

  // 获取属性（考虑状态效果）
  getAttribute(attr) {
    let value = this.state[attr];

    // 应用状态效果的影响
    if (this.state.statusEffects) {
      Object.values(this.state.statusEffects).forEach(effect => {
        if (effect.effects) {
          if (attr === 'maxQi' && effect.effects.maxQi) {
            value += effect.effects.maxQi;
          } else if (attr === 'attack' && effect.effects.attack) {
            value += effect.effects.attack;
          } else if (attr === 'defense' && effect.effects.defense) {
            value += effect.effects.defense;
          } else if (attr === 'divineSenseStrength' && effect.effects.divineSenseStrength) {
            value += effect.effects.divineSenseStrength;
          } else if (attr === 'mana' && effect.effects.mana) {
            value += effect.effects.mana;
          } else if (attr === 'cultivationSpeed' && effect.effects.cultivationSpeed) {
            value += effect.effects.cultivationSpeed;
          }
        }
      });
    }

    return value;
  }

  // 更新属性
  updateAttribute(attr, value) {
    this.state[attr] = value;
    this.saveState();
  }

  // 增加属性
  increaseAttribute(attr, amount) {
    this.state[attr] += amount;
    this.saveState();
  }
}

export default StateManager;
